import { useEffect, useState } from "react";
import { KeyRound, Loader2, Mail, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, put } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ENGINEER_JOB_TYPES, parseList, WORKER_ROLE_OPTIONS } from "@/lib/constants";
import { FieldError, PageHeader } from "@/components/States";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { ChangeEmailDialog } from "@/components/ChangeEmailDialog";
import { LogoUpload } from "@/components/LogoUpload";
import { AvatarUpload } from "@/components/AvatarUpload";
import { EmailPrefsCard } from "@/components/EmailPrefsCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const Card = ({ title, children, testId }) => (
  <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={testId}>
    <h2 className="font-display text-base font-bold text-slate-900 md:text-lg">{title}</h2>
    {children}
  </section>
);

const SaveButton = ({ busy, testId, label = "Save changes" }) => (
  <Button type="submit" disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid={testId}>
    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{label}
  </Button>
);

const toForm = (p) => ({ full_name: p.full_name || "", phone: p.phone || "", areas_covered: p.areas_covered || "", postcodes: (p.postcodes || []).join(", ") });

function DetailsForm() {
  const { profile, refreshProfile } = useAuth();
  const [changingEmail, setChangingEmail] = useState(false);
  const emailFixed = !!profile.support_view || !!profile.is_platform_owner;
  const [f, setF] = useState(() => toForm(profile));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const pcs = parseList(f.postcodes);
    const errs = {};
    if (f.full_name.trim().length < 2) errs.full_name = "Enter your full name";
    if (f.phone.trim() && !/^[0-9+()\-\s]{6,40}$/.test(f.phone.trim())) errs.phone = "Enter a valid telephone number";
    if (pcs.some((p) => !/^[A-Z0-9 ]{1,8}$/.test(p))) errs.postcodes = "Use postcode districts like E13, IG11, separated by commas";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await put("/me/profile", { full_name: f.full_name.trim(), phone: f.phone.trim() || null, areas_covered: f.areas_covered.trim() || null, postcodes: pcs });
      await refreshProfile();
      toast.success("Profile updated");
    } catch (err) {
      setErrors({ form: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Your details" testId="profile-details-card">
      <form onSubmit={submit} noValidate className="space-y-4">
        <AvatarUpload />
        <div>
          <Label>Email</Label>
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            <p className="text-sm text-slate-600" data-testid="profile-email">{profile.email}</p>
            {!emailFixed && <Button type="button" size="sm" variant="outline" onClick={() => setChangingEmail(true)} data-testid="profile-change-email-button"><Mail className="mr-1 h-4 w-4" /> Change email</Button>}
          </div>
          <ChangeEmailDialog open={changingEmail} onOpenChange={setChangingEmail} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="pf-name">Full name *</Label>
            <Input id="pf-name" value={f.full_name} onChange={set("full_name")} className="mt-1.5" data-testid="profile-name-input" />
            <FieldError message={errors.full_name} testId="profile-name-error" />
          </div>
          <div>
            <Label htmlFor="pf-phone">Telephone number</Label>
            <Input id="pf-phone" type="tel" value={f.phone} onChange={set("phone")} className="mt-1.5" data-testid="profile-phone-input" />
            <FieldError message={errors.phone} testId="profile-phone-error" />
          </div>
        </div>
        <div>
          <Label htmlFor="pf-areas">Areas covered</Label>
          <Textarea id="pf-areas" rows={2} value={f.areas_covered} onChange={set("areas_covered")} placeholder="e.g. Newham, Barking, Ilford" className="mt-1.5" data-testid="profile-areas-input" />
        </div>
        <div>
          <Label htmlFor="pf-pcs">Postcodes covered</Label>
          <Input id="pf-pcs" value={f.postcodes} onChange={set("postcodes")} placeholder="E6, E13, IG11" className="font-data mt-1.5 uppercase" data-testid="profile-postcodes-input" />
          <FieldError message={errors.postcodes} testId="profile-postcodes-error" />
        </div>
        {errors.form && <p role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-rose-700" data-testid="profile-form-error">{errors.form}</p>}
        <SaveButton busy={busy} testId="profile-save-button" />
      </form>
    </Card>
  );
}

function CompanyForm() {
  const { profile, refreshProfile } = useAuth();
  const ownerId = profile.company?.owner_user_id;
  const canEditName = !ownerId || ownerId === profile.id;
  const [name, setName] = useState(profile.company?.name || "");
  const [announcement, setAnnouncement] = useState(profile.company?.announcement || "");
  const [enabledTypes, setEnabledTypes] = useState(profile.company?.enabled_user_types || WORKER_ROLE_OPTIONS.map(([role]) => role));
  const [engineerJobTypes, setEngineerJobTypes] = useState(profile.company?.engineer_job_types ?? ENGINEER_JOB_TYPES);
  const [newJobType, setNewJobType] = useState("");
  const [visitTypes, setVisitTypes] = useState(profile.company?.visit_types ?? []);
  const [newVisitType, setNewVisitType] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setName(profile.company?.name || "");
    setAnnouncement(profile.company?.announcement || "");
    setEnabledTypes(profile.company?.enabled_user_types || WORKER_ROLE_OPTIONS.map(([role]) => role));
    setEngineerJobTypes(profile.company?.engineer_job_types ?? ENGINEER_JOB_TYPES);
    setVisitTypes(profile.company?.visit_types ?? []);
  }, [profile.company?.name, profile.company?.announcement, profile.company?.enabled_user_types, profile.company?.engineer_job_types, profile.company?.visit_types]);

  const submit = async (e) => {
    e.preventDefault();
    if (canEditName && name.trim().length < 2) { setError("Enter your company name"); return; }
    if (enabledTypes.includes("engineer") && engineerJobTypes.some((jobType) => !jobType.trim())) { setError("Remove empty Job Types before saving"); return; }
    if (visitTypes.length > 20 || visitTypes.some((visitType) => !visitType.trim())) { setError("Visit Types must contain 1 to 20 non-empty values"); return; }
    setError("");
    setBusy(true);
    try {
      if (canEditName) await put("/company", { name: name.trim(), announcement, enabled_user_types: enabledTypes, engineer_job_types: engineerJobTypes, visit_types: visitTypes });
      else await put("/company/user-types", { announcement, enabled_user_types: enabledTypes, engineer_job_types: engineerJobTypes, visit_types: visitTypes });
      await refreshProfile();
      toast.success("Company settings updated");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Company" testId="profile-company-card">
      <form onSubmit={submit} noValidate className="space-y-4">
        {canEditName && <div className="max-w-md">
          <Label htmlFor="pf-company">Company name *</Label>
          <Input id="pf-company" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="profile-company-input" />
        </div>}
        <div>
          <Label htmlFor="pf-company-announcement">Company announcement</Label>
          <Textarea id="pf-company-announcement" rows={3} maxLength={500} value={announcement} onChange={(e) => setAnnouncement(e.target.value)} placeholder="Share an update with everyone in your company" className="mt-1.5" data-testid="profile-company-announcement-input" />
          <p className="mt-1 text-xs text-slate-500">Visible to all company members on every page. Leave blank to remove it.</p>
        </div>
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-slate-900">Enabled user types</legend>
          <p className="text-xs text-slate-500">Enabled types appear in the sidebar and can be selected when inviting team members.</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {WORKER_ROLE_OPTIONS.filter(([role]) => !(profile.company?.sitewide_disabled_types || []).includes(role)).map(([role, label]) => (
              <label key={role} className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 p-3">
                <Checkbox checked={enabledTypes.includes(role)} onCheckedChange={(checked) => setEnabledTypes((current) => checked ? [...current, role] : current.filter((value) => value !== role))} data-testid={`company-user-type-${role}`} />
                <span className="text-sm font-medium text-slate-800">{label}</span>
              </label>
            ))}
          </div>
        </fieldset>
        {enabledTypes.includes("engineer") && <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-slate-900">Engineer Job Types</legend>
          <p className="text-xs text-slate-500">These options appear in the Job Type field on Engineer warrant addresses.</p>
          <div className="flex gap-2">
            <Input value={newJobType} onChange={(e) => setNewJobType(e.target.value)} maxLength={60} placeholder="Add a Job Type" aria-label="New Engineer Job Type" data-testid="company-engineer-job-type-input" />
            <Button type="button" size="icon" variant="outline" aria-label="Add Job Type" title="Add Job Type" data-testid="company-engineer-job-type-add" onClick={() => {
              const next = newJobType.trim();
              if (!next || engineerJobTypes.some((value) => value.toLowerCase() === next.toLowerCase())) return;
              setEngineerJobTypes((current) => [...current, next]);
              setNewJobType("");
            }}><Plus className="h-4 w-4" /></Button>
          </div>
          {engineerJobTypes.length > 0 && <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
            {engineerJobTypes.map((jobType) => (
              <li key={jobType} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`company-engineer-job-type-${jobType}`}>
                <span>{jobType}</span>
                <Button type="button" size="icon" variant="ghost" aria-label={`Remove ${jobType}`} title="Remove Job Type" data-testid={`company-engineer-job-type-remove-${jobType}`} onClick={() => setEngineerJobTypes((current) => current.filter((value) => value !== jobType))}>
                  <X className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>}
        </fieldset>}
        {enabledTypes.includes("agent") && <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-slate-900">Visit Types</legend>
          <p className="text-xs text-slate-500">These options appear when managers add Pre Warrant Visits.</p>
          <div className="flex gap-2">
            <Input value={newVisitType} onChange={(e) => setNewVisitType(e.target.value)} maxLength={60} placeholder="Add a Visit Type" aria-label="New Visit Type" data-testid="company-visit-type-input" />
            <Button type="button" size="icon" variant="outline" aria-label="Add Visit Type" title="Add Visit Type" disabled={!newVisitType.trim() || visitTypes.length >= 20} data-testid="company-visit-type-add" onClick={() => {
              const next = newVisitType.trim();
              if (!next || visitTypes.length >= 20 || visitTypes.some((value) => value.toLowerCase() === next.toLowerCase())) return;
              setVisitTypes((current) => [...current, next]);
              setNewVisitType("");
            }}><Plus className="h-4 w-4" /></Button>
          </div>
          {visitTypes.length > 0 ? <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
            {visitTypes.map((visitType) => (
              <li key={visitType} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`company-visit-type-${visitType}`}>
                <span>{visitType}</span>
                <Button type="button" size="icon" variant="ghost" aria-label={`Remove ${visitType}`} title="Remove Visit Type" data-testid={`company-visit-type-remove-${visitType}`} onClick={() => setVisitTypes((current) => current.filter((value) => value !== visitType))}>
                  <X className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul> : <p className="text-sm text-slate-500">No Visit Types configured.</p>}
        </fieldset>}
        <FieldError message={error} testId="profile-company-error" />
        <SaveButton busy={busy} testId="profile-company-save-button" label="Save company settings" />
      </form>
      {canEditName && <div className="border-t border-slate-100 pt-4"><LogoUpload /></div>}
    </Card>
  );
}

export default function Profile() {
  const { profile } = useAuth();
  const [changing, setChanging] = useState(false);
  return (
    <div className="max-w-3xl space-y-6" data-testid="profile-page">
      <PageHeader eyebrow="Account" title="Profile" description="Update your details, picture, emails and password." />
      <DetailsForm />
      {profile.role === "manager" && profile.company_id && <CompanyForm />}
      {profile.company_id && <EmailPrefsCard Card={Card} />}
      <Card title="Password" testId="profile-password-card">
        <p className="text-sm text-slate-600">Change the password you use to sign in.</p>
        <Button variant="outline" onClick={() => setChanging(true)} data-testid="profile-change-password-button"><KeyRound className="mr-1 h-4 w-4" /> Change password</Button>
      </Card>
      <ChangePasswordDialog open={changing} onOpenChange={setChanging} />
    </div>
  );
}
